import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SafeIcon from '../common/SafeIcon';

export default function VibeCode() {
  const buildSteps = [
    { title: "Business Problem", desc: "Identify the friction point.", icon: "Target" },
    { title: "Clear Prompt", desc: "Define the tone and rules.", icon: "MessageSquare" },
    { title: "Visual Direction", desc: "Set the aesthetic vibe.", icon: "Layout" },
    { title: "Interface Mockup", desc: "Design the interaction.", icon: "Smartphone" },
    { title: "Conversion Path", desc: "Drive the next action.", icon: "TrendingUp" }
  ];

  const agentExamples = [
    { name: "Ruby Replies", role: "Review Reply Assistant", color: "text-[#409FA1]", bg: "bg-[#409FA1]/10", icon: "MessageCircle" },
    { name: "Annie Amplifies", role: "Social Post Assistant", color: "text-[#F49558]", bg: "bg-[#F49558]/10", icon: "Share2" },
    { name: "Mabel Writes", role: "Website Article Writer", color: "text-[#2CB5B0]", bg: "bg-[#2CB5B0]/10", icon: "FileText" },
    { name: "RepUpgrade AI", role: "Reputation Brain", color: "text-[#244A49]", bg: "bg-[#244A49]/10", icon: "Cpu" }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F8F7] font-lexend text-[#101F1F]">
      <Navbar />
      
      <main className="flex-grow pt-32 pb-0 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-[0.02]" style={{backgroundImage: 'linear-gradient(#244A49 1px, transparent 1px), linear-gradient(90deg, #244A49 1px, transparent 1px)', backgroundSize: '32px 32px'}} />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 mb-24">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24"
          >
            <div className="flex-1 text-center lg:text-left space-y-8">
              <div className="space-y-6">
                <h1 className="text-4xl lg:text-6xl font-bold tracking-tight text-[#101F1F] leading-tight">
                  Ruby Replies: <br className="hidden lg:block"/>
                  <span className="text-[#244A49]">From Idea to Interface</span>
                </h1>
                <p className="text-xl lg:text-2xl text-[#244A49]/90 font-medium max-w-2xl mx-auto lg:mx-0">
                  A review reply assistant concept turned into a polished 9:16 product visual using vibe-coded direction.
                </p>
                <p className="text-lg text-gray-600 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                  Ruby Replies shows the core promise of vibe coding: you do not need to start with perfect code. You start with clear language, a real business problem, and a visual direction strong enough for AI to build from.
                </p>
              </div>
              
              <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                {['Prompted from strategy', 'Designed for conversion', 'Built for reputation'].map((chip, idx) => (
                  <motion.div key={idx} whileHover={{ scale: 1.05 }} className="px-5 py-2.5 bg-white rounded-full border border-[#2CB5B0]/20 shadow-sm text-sm font-michroma text-[#244A49] flex items-center gap-2">
                    <SafeIcon name="Check" className="w-4 h-4 text-[#409FA1]" />
                    {chip}
                  </motion.div>
                ))}
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-6">
                <Link to="/create-access" className="px-8 py-4 bg-[#F49558] hover:bg-[#D35E0E] text-white font-semibold rounded-xl text-center shadow-lg shadow-[#F49558]/20 transition-all text-lg">
                  Create RepUpgrade Access
                </Link>
                <Link to="/ai-team/ruby" className="px-8 py-4 bg-transparent border-2 border-[#244A49] text-[#244A49] hover:bg-[#244A49]/5 font-semibold rounded-xl text-center transition-all text-lg">
                  Meet Ruby Replies
                </Link>
              </div>
            </div>

            <div className="flex-1 relative max-w-[340px] w-full mx-auto py-10">
              <div className="absolute inset-0 pointer-events-none z-20 flex items-center justify-center scale-110">
                <svg className="absolute w-[130%] h-[110%] overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <motion.path 
                    d="M 20,10 C 60,-10 90,20 80,60 C 70,100 30,110 10,70 C -10,30 0,20 20,10 Z" 
                    fill="transparent" stroke="#409FA1" strokeWidth="0.4" strokeDasharray="200" strokeDashoffset="0"
                    style={{ filter: 'drop-shadow(0 0 6px rgba(64,159,161,0.6))' }}
                    animate={{ strokeDashoffset: [200, 0, -200], scale: [1, 0.96, 1], rotate: [0, 3, -2, 0] }}
                    transition={{ strokeDashoffset: { duration: 10, repeat: Infinity, ease: "linear" }, scale: { duration: 4, repeat: Infinity, ease: "easeInOut" }, rotate: { duration: 7, repeat: Infinity, ease: "easeInOut" } }}
                  />
                  <motion.circle cx="15" cy="15" r="1.5" fill="#F6D541" animate={{ opacity: [0, 1, 0], scale: [0, 1.5, 0] }} transition={{ duration: 4, repeat: Infinity, delay: 1, ease: "easeOut" }}/>
                  <motion.circle cx="85" cy="20" r="1.5" fill="#F6D541" animate={{ opacity: [0, 1, 0], scale: [0, 1.5, 0] }} transition={{ duration: 4, repeat: Infinity, delay: 2.5, ease: "easeOut" }}/>
                  <motion.circle cx="80" cy="85" r="1.5" fill="#F6D541" animate={{ opacity: [0, 1, 0], scale: [0, 1.5, 0] }} transition={{ duration: 4, repeat: Infinity, delay: 1.8, ease: "easeOut" }}/>
                </svg>
              </div>
              
              <div className="relative aspect-[9/16] rounded-[2.5rem] p-2 bg-white shadow-2xl shadow-[#409FA1]/10 border border-gray-100 z-10 group">
                <div className="absolute inset-0 rounded-[2.5rem] shadow-[0_0_40px_rgba(44,181,176,0.15)] opacity-50 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
                <div className="w-full h-full rounded-[2rem] overflow-hidden bg-gray-50 relative">
                  <img 
                    src="/images/ruby-replies-showcase.png" 
                    onError={(e) => { 
                      e.target.onerror = null; 
                      e.target.src = "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80"; 
                    }} 
                    alt="Ruby Replies Mockup" 
                    className="w-full h-full object-cover transform group-hover:scale-[1.02] transition-transform duration-700" 
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <section className="py-24 bg-white border-t border-gray-100">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#101F1F] mb-6">What Was Vibe-Coded</h2>
            <p className="text-lg md:text-xl text-[#101F1F]/70 leading-relaxed font-medium">
              Ruby Replies started as a simple local business problem: reviews need thoughtful replies, but owners do not have time to stare at a blank box.
            </p>
          </div>
        </section>

        <section className="py-24 bg-[#F7F8F7] border-y border-gray-100">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#101F1F] mb-6">The Build Pattern</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {buildSteps.map((step, idx) => (
                <motion.div 
                  key={idx} 
                  initial={{ opacity: 0, y: 20 }} 
                  whileInView={{ opacity: 1, y: 0 }} 
                  viewport={{ once: true }} 
                  transition={{ delay: idx * 0.1 }} 
                  className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm text-center relative hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 mx-auto bg-[#244A49]/5 rounded-xl flex items-center justify-center mb-4 text-[#409FA1]">
                    <SafeIcon name={step.icon} className="w-6 h-6" />
                  </div>
                  <div className="text-xs font-michroma text-[#F49558] mb-3">Step 0{idx + 1}</div>
                  <h3 className="font-bold text-[#101F1F] mb-2">{step.title}</h3>
                  <p className="text-sm text-[#101F1F]/60">{step.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 bg-white">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#101F1F] mb-6">More Examples Coming</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {agentExamples.map((agent, idx) => (
                <div key={idx} className="bg-[#F7F8F7] p-8 rounded-3xl border border-gray-100 text-center hover:border-[#409FA1]/30 transition-colors group cursor-default">
                  <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110 ${agent.bg} ${agent.color}`}>
                    <SafeIcon name={agent.icon} className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-[#101F1F] mb-2">{agent.name}</h3>
                  <p className="text-sm text-[#101F1F]/60 font-medium">{agent.role}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}